import { Caption1, Text, makeStyles, mergeClasses } from '@fluentui/react-components';
import { ShellIcons, tokens } from '@vigeo/ui';

const useStyles = makeStyles({
  /**
   * Equal segments across the full width, as the frame draws them. `flexDirection` is named
   * because Griffel merges per property and a `display:flex` that inherits a column direction
   * from anywhere would stack the four stages on top of each other.
   */
  tracker: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', gap: tokens.spacingHorizontalXS },
  segment: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, flexBasis: '7rem', flexGrow: 1, minWidth: 0 },
  /** 0.25rem, not 4px: feature code may not carry a px literal (CLAUDE.md rule 2). */
  bar: { height: '0.25rem', borderRadius: tokens.borderRadiusSmall, backgroundColor: tokens.colorNeutralStroke2 },
  barReached: { backgroundColor: tokens.colorBrandBackground },
  label: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: tokens.spacingHorizontalXXS, minWidth: 0 },
  icon: { display: 'flex', flexDirection: 'row', alignItems: 'center', color: tokens.colorBrandForeground1 },
  /** The stage being viewed is the only one in semibold, per the frame. */
  current: { fontWeight: tokens.fontWeightSemibold },
  ahead: { color: tokens.colorNeutralForeground3 },
  caption: { color: tokens.colorNeutralForeground2 },
});

export interface OfferTrackerProps {
  readonly steps: readonly string[];
  /** Index of the stage reached. Everything before it is done, everything after is ahead. */
  readonly reached: number;
  /** The frame's second line under each stage, on row 2.2's lifecycle strip only. */
  readonly captions?: readonly string[];
  /**
   * Whether the stage reached is itself finished, and so carries a tick too.
   *
   * Frames differ on this and both are approved. Rows 2.1/2.2 tick only the stages BEFORE the one
   * reached (the default). Frame 06b `297:932` — an offer that has been reviewed and is waiting to
   * go out — ticks Draft AND Reviewed, because the review is done and "Sent" is what is next.
   * Frame `295:801`, a draft being read, ticks nothing at all, which both rules already agree on.
   */
  readonly tickReached?: boolean;
  readonly label?: string;
}

/**
 * The four-segment progress strip on both 06b rows — Draft/Reviewed/Sent/Signed for the offer,
 * Candidate/Onboarding/Active for the lifecycle.
 *
 * One component for both because the frames draw them identically: an equal-width bar per stage,
 * filled up to and including the one reached, with a tick on every completed stage and the
 * current one in semibold. A second implementation would drift the moment either frame moved.
 *
 * `ol`, not a row of divs: this is an ordered sequence of stages and a screen reader should be
 * able to count it. The bars are decoration and carry no role of their own.
 */
export function OfferTracker({ steps, reached, captions, label, tickReached = false }: OfferTrackerProps) {
  const styles = useStyles();
  return (
    <ol className={styles.tracker} aria-label={label ?? 'Progress'}>
      {steps.map((step, index) => {
        const done = tickReached ? index <= reached : index < reached;
        const current = index === reached;
        return (
          <li key={step} className={styles.segment}>
            <div className={mergeClasses(styles.bar, index <= reached && styles.barReached)} aria-hidden="true" />
            <div className={styles.label}>
              {done ? <span className={styles.icon}><ShellIcons.check /></span> : null}
              <Text className={mergeClasses(current && styles.current, index > reached && styles.ahead)}>{step}</Text>
            </div>
            {captions?.[index] === undefined ? null : <Caption1 className={styles.caption}>{captions[index]}</Caption1>}
          </li>
        );
      })}
    </ol>
  );
}
